import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  FormControl, 
  FormLabel, 
  RadioGroup, 
  FormControlLabel, 
  Radio, 
  Button,
  TextField,
  Container,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Link,
  FormGroup,
  Checkbox
} from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { neon } from '@neondatabase/serverless';
import AssessmentIcon from '@mui/icons-material/Assessment';

// Get the database URL from environment variables or use direct string for development
const getDatabaseUrl = () => {
  return 'postgres://neondb_owner:npg_Ub9EiNceZ3ro@ep-divine-sun-a18caqng-pooler.ap-southeast-1.aws.neon.tech/neondb';
};

// Function to parse query parameters
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const POCEvaluationForm = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const conversationId = query.get('conversationId');
  
  console.log('Debug - Conversation ID from URL:', conversationId); // Debug log
  
  const [formData, setFormData] = useState({
    teachingAccuracy: '',
    nodeEvaluation: '',
    errorCorrectionEvaluation: '',
    checkpointEvaluation: '',
    technicalSuggestions: '',
    teachingIssues: {
      scriptMismatch: false,
      impreciseContent: false,
      inappropriateContext: false,
      ambiguousContent: false,
      other: false
    },
    nodeIssues: {
      falsePositive: false,
      falseNegative: false,
      inconsistentJudgment: false,
      delayedJudgment: false,
      other: false
    },
    errorCorrectionIssues: {
      missedErrors: false,
      noSuggestions: false,
      noContext: false,
      unclearSuggestions: false,
      impracticalSuggestions: false,
      other: false
    },
    checkpointIssues: {
      falsePass: false,
      falseFail: false,
      unreasonableDelay: false,
      unclearDelay: false,
      other: false
    }
  });

  // Add state for other issue descriptions
  const [otherIssueDetails, setOtherIssueDetails] = useState({
    teachingOtherDetails: '',
    nodeOtherDetails: '',
    errorCorrectionOtherDetails: '',
    checkpointOtherDetails: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [databaseUrl] = useState(() => getDatabaseUrl());
  const [memberId, setMemberId] = useState(null);
  
  // Get member_id from localStorage when component mounts
  useEffect(() => {
    const getMemberId = () => {
      const userData = localStorage.getItem('user');
      console.log('Debug - Raw user data from localStorage:', userData); // Debug log
      if (userData) {
        try {
          const user = JSON.parse(userData);
          console.log('Debug - Parsed user object:', user); // Debug log
          if (user && user.id) {
            console.log('Debug - User ID:', user.id); // Debug log
            setMemberId(user.id);
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      } else {
        console.log('Debug - No user data found in localStorage'); // Debug log
      }
    };

    getMemberId();
  }, []);

  // Initialize the database table on component mount
  useEffect(() => {
    const setupDatabase = async () => {
      await initDatabase();
      await ensureConversationIdColumn();
      await ensureMemberIdColumn();
    };
    
    setupDatabase();
  }, []);

  // Function to initialize the database table
  const initDatabase = async () => {
    setIsLoading(true);
    
    try {
      if (!databaseUrl) {
        throw new Error('No database URL available');
      }
      
      const sql = neon(databaseUrl, { ssl: false });
      
      // Test connection
      const testResult = await sql`SELECT 1 as test`;
      console.log('Connection test result:', testResult);
      setConnectionStatus('Connected to database');
      
      // Create the evaluation_forms table if it doesn't exist (update to include member_id)
      await sql`
        CREATE TABLE IF NOT EXISTS evaluation_forms (
          id SERIAL PRIMARY KEY,
          member_id BIGINT,
          conversation_id BIGINT,
          teaching_accuracy TEXT NOT NULL,
          node_evaluation TEXT NOT NULL,
          error_correction_evaluation TEXT NOT NULL,
          checkpoint_evaluation TEXT NOT NULL,
          technical_suggestions TEXT,
          teaching_issues JSON,
          node_issues JSON,
          error_correction_issues JSON,
          checkpoint_issues JSON,
          created_at TIMESTAMP WITH TIME ZONE DEFAULT (CURRENT_TIMESTAMP AT TIME ZONE 'Asia/Taipei')
        )
      `;
      
    } catch (err) {
      console.error('Error initializing database:', err);
      setConnectionStatus(`Database connection failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to add conversation_id column if it's missing
  const ensureConversationIdColumn = async () => {
    try {
      if (!databaseUrl) {
        throw new Error('No database URL available');
      }
      
      const sql = neon(databaseUrl, { ssl: false });
      
      // Check if conversation_id column exists in evaluation_forms table
      const columnCheck = await sql`
        SELECT column_name 
        FROM information_schema.columns 
        WHERE table_name = 'evaluation_forms' 
        AND column_name = 'conversation_id';
      `;
      
      // If column doesn't exist, add it
      if (columnCheck.length === 0) {
        console.log('Adding conversation_id column to evaluation_forms table');
        await sql`
          ALTER TABLE evaluation_forms 
          ADD COLUMN conversation_id BIGINT;
        `;
        console.log('conversation_id column added successfully');
      } else {
        console.log('conversation_id column already exists');
      }
      
    } catch (err) {
      console.error('Error checking/adding conversation_id column:', err);
    }
  };

  // Function to add member_id column if it's missing
  const ensureMemberIdColumn = async () => {
    try {
      if (!databaseUrl) {
        throw new Error('No database URL available');
      }
      
      const sql = neon(databaseUrl, { ssl: false });
      
      // Check if member_id column exists in evaluation_forms table
      const columnCheck = await sql`
        SELECT column_name 
        FROM information_schema.columns 
        WHERE table_name = 'evaluation_forms' 
        AND column_name = 'member_id';
      `;
      
      // If column doesn't exist, add it
      if (columnCheck.length === 0) {
        console.log('Adding member_id column to evaluation_forms table');
        await sql`
          ALTER TABLE evaluation_forms 
          ADD COLUMN member_id BIGINT;
        `;
        console.log('member_id column added successfully');
      } else {
        console.log('member_id column already exists');
      }
      
    } catch (err) {
      console.error('Error checking/adding member_id column:', err);
    }
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    
    if (type === 'checkbox') {
      // Handle checkbox changes
      const [section, field] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: checked
        }
      }));
    } else if (name.includes('OtherDetails')) {
      // Handle other details textarea changes
      setOtherIssueDetails(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      // Handle radio button changes
      const newFormData = {
        ...formData,
        [name]: value
      };
      
      // Reset checkbox values if changing radio selections to values that don't show checkboxes
      if (name === 'teachingAccuracy' && value !== 'acceptable' && value !== 'needsImprovement' && value !== 'insufficient') {
        newFormData.teachingIssues = {
          scriptMismatch: false,
          impreciseContent: false,
          inappropriateContext: false,
          ambiguousContent: false,
          other: false
        };
      } else if (name === 'nodeEvaluation' && value !== 'acceptable' && value !== 'needsImprovement' && value !== 'insufficient') {
        newFormData.nodeIssues = {
          falsePositive: false,
          falseNegative: false,
          inconsistentJudgment: false,
          delayedJudgment: false,
          other: false
        };
      } else if (name === 'errorCorrectionEvaluation' && value !== 'acceptable' && value !== 'needsImprovement' && value !== 'insufficient') {
        newFormData.errorCorrectionIssues = {
          missedErrors: false,
          noSuggestions: false,
          noContext: false,
          unclearSuggestions: false,
          impracticalSuggestions: false,
          other: false
        };
      } else if (name === 'checkpointEvaluation' && value !== 'acceptable' && value !== 'needsImprovement' && value !== 'insufficient') {
        newFormData.checkpointIssues = {
          falsePass: false,
          falseFail: false,
          unreasonableDelay: false,
          unclearDelay: false,
          other: false
        };
      }
      
      setFormData(newFormData);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validate form data
    const requiredFields = ['teachingAccuracy', 'nodeEvaluation', 'errorCorrectionEvaluation', 'checkpointEvaluation'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      setSnackbar({
        open: true,
        message: '請填寫所有評估項目',
        severity: 'error'
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      if (!databaseUrl) {
        throw new Error('No database URL available');
      }
      
      const sql = neon(databaseUrl, { ssl: false });
      
      // Get member_id from localStorage
      const userData = localStorage.getItem('user');
      console.log('Debug - Raw user data before submission:', userData); // Debug log
      let currentMemberId = null;
      
      if (userData) {
        try {
          const user = JSON.parse(userData);
          currentMemberId = user.id;
          console.log('Debug - Current member ID before submission:', currentMemberId); // Debug log
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }

      // Prepare issue data with other details
      const teachingIssuesWithDetails = {
        ...formData.teachingIssues,
        otherDetails: formData.teachingIssues.other ? otherIssueDetails.teachingOtherDetails : ''
      };

      const nodeIssuesWithDetails = {
        ...formData.nodeIssues,
        otherDetails: formData.nodeIssues.other ? otherIssueDetails.nodeOtherDetails : ''
      };

      const errorCorrectionIssuesWithDetails = {
        ...formData.errorCorrectionIssues,
        otherDetails: formData.errorCorrectionIssues.other ? otherIssueDetails.errorCorrectionOtherDetails : ''
      };

      const checkpointIssuesWithDetails = {
        ...formData.checkpointIssues,
        otherDetails: formData.checkpointIssues.other ? otherIssueDetails.checkpointOtherDetails : ''
      };
      
      // Insert form data into the database (including member_id and conversation_id)
      const insertQuery = sql`
        INSERT INTO evaluation_forms (
          member_id,
          conversation_id,
          teaching_accuracy,
          node_evaluation,
          error_correction_evaluation,
          checkpoint_evaluation,
          technical_suggestions,
          teaching_issues,
          node_issues,
          error_correction_issues,
          checkpoint_issues,
          created_at
        ) VALUES (
          ${currentMemberId ? parseInt(currentMemberId) : null},
          ${conversationId ? parseInt(conversationId) : null},
          ${formData.teachingAccuracy},
          ${formData.nodeEvaluation},
          ${formData.errorCorrectionEvaluation},
          ${formData.checkpointEvaluation},
          ${formData.technicalSuggestions || ''},
          ${JSON.stringify(teachingIssuesWithDetails)},
          ${JSON.stringify(nodeIssuesWithDetails)},
          ${JSON.stringify(errorCorrectionIssuesWithDetails)},
          ${JSON.stringify(checkpointIssuesWithDetails)},
          (CURRENT_TIMESTAMP AT TIME ZONE 'Asia/Taipei')
        )
      `;
      
      console.log('Debug - SQL query parameters:', {
        member_id: currentMemberId ? parseInt(currentMemberId) : null,
        conversation_id: conversationId ? parseInt(conversationId) : null
      }); // Debug log
      
      await insertQuery;
      
      // Reset form after successful submission
      setFormData({
        teachingAccuracy: '',
        nodeEvaluation: '',
        errorCorrectionEvaluation: '',
        checkpointEvaluation: '',
        technicalSuggestions: '',
        teachingIssues: {
          scriptMismatch: false,
          impreciseContent: false,
          inappropriateContext: false,
          ambiguousContent: false,
          other: false
        },
        nodeIssues: {
          falsePositive: false,
          falseNegative: false,
          inconsistentJudgment: false,
          delayedJudgment: false,
          other: false
        },
        errorCorrectionIssues: {
          missedErrors: false,
          noSuggestions: false,
          noContext: false,
          unclearSuggestions: false,
          impracticalSuggestions: false,
          other: false
        },
        checkpointIssues: {
          falsePass: false,
          falseFail: false,
          unreasonableDelay: false,
          unclearDelay: false,
          other: false
        }
      });

      setOtherIssueDetails({
        teachingOtherDetails: '',
        nodeOtherDetails: '',
        errorCorrectionOtherDetails: '',
        checkpointOtherDetails: ''
      });
      
      setSnackbar({
        open: true,
        message: '表單已成功提交',
        severity: 'success'
      });
      
      // Navigate to results page after successful submission
      setTimeout(() => {
        navigate('/poc-evaluation-results');
      }, 2000);
      
    } catch (err) {
      console.error('Error submitting form:', err);
      setSnackbar({
        open: true,
        message: `提交失敗: ${err.message}`,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" fontWeight="bold">
          POC系統驗測表單
        </Typography>
        <Typography variant="subtitle1" gutterBottom align="center" sx={{ mb: 2 }}>
          請根據您的測試經驗，提供量化的評估結果
        </Typography>
        
        {conversationId && (
          <Typography variant="subtitle2" gutterBottom align="center" sx={{ mb: 4, color: 'text.secondary' }}>
            評估對話 ID: {conversationId}
          </Typography>
        )}

        {connectionStatus && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <Typography 
              color={connectionStatus.includes('failed') ? 'error' : 'success.main'}
              variant="body2"
            >
              {connectionStatus}
            </Typography>
          </Box>
        )}

        <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold">
            技術需求達成度評估
          </Typography>
          <Typography variant="body1" paragraph>
            本區塊評估系統的技術功能是否符合預期目標，請依據您的測試經驗，選擇最符合的準確率區間
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            {/* Teaching Guidance Accuracy */}
            <FormControl component="fieldset" sx={{ mb: 4, width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                教學引導準確性：
              </FormLabel>
              <Typography variant="body2" sx={{ mb: 2 }}>
                評估陪練模式下每個情境單元的教學引導是否與富邦提供的範例腳本相符
              </Typography>
              <RadioGroup
                name="teachingAccuracy"
                value={formData.teachingAccuracy}
                onChange={handleChange}
                sx={{ ml: 2 }}
              >
                <FormControlLabel value="excellent" control={<Radio />} label="優良 (>95%)" />
                <FormControlLabel value="good" control={<Radio />} label="良好 (85-95%)" />
                <FormControlLabel value="acceptable" control={<Radio />} label="可接受 (70-85%)" />
                <FormControlLabel value="needsImprovement" control={<Radio />} label="需改進 (50-70%)" />
                <FormControlLabel value="insufficient" control={<Radio />} label="不足 (<50%)" />
              </RadioGroup>
              
              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 2, 
                  mb: 1, 
                  display: formData.teachingAccuracy === 'acceptable' || formData.teachingAccuracy === 'needsImprovement' || formData.teachingAccuracy === 'insufficient' ? 'block' : 'none' 
                }}
              >
                發現的問題：（可複選）
              </Typography>
              <FormGroup sx={{ ml: 2, display: formData.teachingAccuracy === 'acceptable' || formData.teachingAccuracy === 'needsImprovement' || formData.teachingAccuracy === 'insufficient' ? 'block' : 'none' }}>
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.teachingIssues.scriptMismatch}
                    onChange={handleChange}
                    name="teachingIssues.scriptMismatch"
                  />} 
                  label="建議內容與腳本不符" 
                />
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.teachingIssues.impreciseContent}
                    onChange={handleChange}
                    name="teachingIssues.impreciseContent"
                  />} 
                  label="建議內容不夠精確" 
                />
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.teachingIssues.inappropriateContext}
                    onChange={handleChange}
                    name="teachingIssues.inappropriateContext"
                  />} 
                  label="建議不適用當前情境" 
                />
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.teachingIssues.ambiguousContent}
                    onChange={handleChange}
                    name="teachingIssues.ambiguousContent"
                  />} 
                  label="建議內容語意模糊" 
                />
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.teachingIssues.other}
                    onChange={handleChange}
                    name="teachingIssues.other"
                  />} 
                  label="其他" 
                />
                {formData.teachingIssues.other && (
                  <TextField
                    fullWidth
                    name="teachingOtherDetails"
                    label="請說明其他問題"
                    multiline
                    rows={2}
                    value={otherIssueDetails.teachingOtherDetails}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{ mt: 1, mb: 2 }}
                  />
                )}
              </FormGroup>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            {/* Node Evaluation Accuracy */}
            <FormControl component="fieldset" sx={{ mb: 4, width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                節點判斷正確性：
              </FormLabel>
              <Typography variant="body2" sx={{ mb: 2 }}>
                評估系統是否能正確判斷訓練情境中各個節點的完成狀態
              </Typography>
              <RadioGroup
                name="nodeEvaluation"
                value={formData.nodeEvaluation}
                onChange={handleChange}
                sx={{ ml: 2 }}
              >
                <FormControlLabel value="excellent" control={<Radio />} label="優良 (>95%)" />
                <FormControlLabel value="good" control={<Radio />} label="良好 (85-95%)" />
                <FormControlLabel value="acceptable" control={<Radio />} label="可接受 (70-85%)" />
                <FormControlLabel value="needsImprovement" control={<Radio />} label="需改進 (50-70%)" />
                <FormControlLabel value="insufficient" control={<Radio />} label="不足 (<50%)" />
              </RadioGroup>

              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 2, 
                  mb: 1, 
                  display: formData.nodeEvaluation === 'acceptable' || formData.nodeEvaluation === 'needsImprovement' || formData.nodeEvaluation === 'insufficient' ? 'block' : 'none' 
                }}
              >
                發現的問題：（可複選）
              </Typography>
              <FormGroup sx={{ ml: 2, display: formData.nodeEvaluation === 'acceptable' || formData.nodeEvaluation === 'needsImprovement' || formData.nodeEvaluation === 'insufficient' ? 'block' : 'none' }}>
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.nodeIssues.falsePositive}
                    onChange={handleChange}
                    name="nodeIssues.falsePositive"
                  />} 
                  label="誤判判斷為達標" 
                />
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.nodeIssues.falseNegative}
                    onChange={handleChange}
                    name="nodeIssues.falseNegative"
                  />} 
                  label="誤判判斷為未達標" 
                />
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.nodeIssues.inconsistentJudgment}
                    onChange={handleChange}
                    name="nodeIssues.inconsistentJudgment"
                  />} 
                  label="誤判判斷不一致" 
                />
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.nodeIssues.delayedJudgment}
                    onChange={handleChange}
                    name="nodeIssues.delayedJudgment"
                  />} 
                  label="誤判判斷延遲" 
                />
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.nodeIssues.other}
                    onChange={handleChange}
                    name="nodeIssues.other"
                  />} 
                  label="其他" 
                />
                {formData.nodeIssues.other && (
                  <TextField
                    fullWidth
                    name="nodeOtherDetails"
                    label="請說明其他問題"
                    multiline
                    rows={2}
                    value={otherIssueDetails.nodeOtherDetails}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{ mt: 1, mb: 2 }}
                  />
                )}
              </FormGroup>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            {/* Error Correction Evaluation */}
            <FormControl component="fieldset" sx={{ mb: 4, width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                評估報告的糾錯狀況：
              </FormLabel>
              <Typography variant="body2" sx={{ mb: 2 }}>
                評估系統對常見錯誤和違反全域規則的識別能力，以及提供的糾錯建議品質
              </Typography>
              <RadioGroup
                name="errorCorrectionEvaluation"
                value={formData.errorCorrectionEvaluation}
                onChange={handleChange}
                sx={{ ml: 2 }}
              >
                <FormControlLabel value="excellent" control={<Radio />} label="優良 (>95%)" />
                <FormControlLabel value="good" control={<Radio />} label="良好 (85-95%)" />
                <FormControlLabel value="acceptable" control={<Radio />} label="可接受 (70-85%)" />
                <FormControlLabel value="needsImprovement" control={<Radio />} label="需改進 (50-70%)" />
                <FormControlLabel value="insufficient" control={<Radio />} label="不足 (<50%)" />
              </RadioGroup>

              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 2, 
                  mb: 1, 
                  display: formData.errorCorrectionEvaluation === 'acceptable' || formData.errorCorrectionEvaluation === 'needsImprovement' || formData.errorCorrectionEvaluation === 'insufficient' ? 'block' : 'none' 
                }}
              >
                發現的問題：（可複選）
              </Typography>
              <FormGroup sx={{ ml: 2, display: formData.errorCorrectionEvaluation === 'acceptable' || formData.errorCorrectionEvaluation === 'needsImprovement' || formData.errorCorrectionEvaluation === 'insufficient' ? 'block' : 'none' }}>
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.errorCorrectionIssues.missedErrors}
                    onChange={handleChange}
                    name="errorCorrectionIssues.missedErrors"
                  />} 
                  label="未能識別特定類型錯誤" 
                />
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.errorCorrectionIssues.noSuggestions}
                    onChange={handleChange}
                    name="errorCorrectionIssues.noSuggestions"
                  />} 
                  label="未能提供修改建議" 
                />
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.errorCorrectionIssues.noContext}
                    onChange={handleChange}
                    name="errorCorrectionIssues.noContext"
                  />} 
                  label="未能提供使用情境" 
                />
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.errorCorrectionIssues.unclearSuggestions}
                    onChange={handleChange}
                    name="errorCorrectionIssues.unclearSuggestions"
                  />} 
                  label="建議過於不明確" 
                />
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.errorCorrectionIssues.impracticalSuggestions}
                    onChange={handleChange}
                    name="errorCorrectionIssues.impracticalSuggestions"
                  />} 
                  label="系統建議不實用" 
                />
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.errorCorrectionIssues.other}
                    onChange={handleChange}
                    name="errorCorrectionIssues.other"
                  />} 
                  label="其他" 
                />
                {formData.errorCorrectionIssues.other && (
                  <TextField
                    fullWidth
                    name="errorCorrectionOtherDetails"
                    label="請說明其他問題"
                    multiline
                    rows={2}
                    value={otherIssueDetails.errorCorrectionOtherDetails}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{ mt: 1, mb: 2 }}
                  />
                )}
              </FormGroup>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            {/* Checkpoint Evaluation */}
            <FormControl component="fieldset" sx={{ mb: 4, width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                查核點判斷準確性：
              </FormLabel>
              <Typography variant="body2" sx={{ mb: 2 }}>
                評估系統是否能正確判斷各節點內查核點的達成情況
              </Typography>
              <RadioGroup
                name="checkpointEvaluation"
                value={formData.checkpointEvaluation}
                onChange={handleChange}
                sx={{ ml: 2 }}
              >
                <FormControlLabel value="excellent" control={<Radio />} label="優良 (>95%)" />
                <FormControlLabel value="good" control={<Radio />} label="良好 (85-95%)" />
                <FormControlLabel value="acceptable" control={<Radio />} label="可接受 (70-85%)" />
                <FormControlLabel value="needsImprovement" control={<Radio />} label="需改進 (50-70%)" />
                <FormControlLabel value="insufficient" control={<Radio />} label="不足 (<50%)" />
              </RadioGroup>

              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 2, 
                  mb: 1, 
                  display: formData.checkpointEvaluation === 'acceptable' || formData.checkpointEvaluation === 'needsImprovement' || formData.checkpointEvaluation === 'insufficient' ? 'block' : 'none' 
                }}
              >
                發現的問題：（可複選）
              </Typography>
              <FormGroup sx={{ ml: 2, display: formData.checkpointEvaluation === 'acceptable' || formData.checkpointEvaluation === 'needsImprovement' || formData.checkpointEvaluation === 'insufficient' ? 'block' : 'none' }}>
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.checkpointIssues.falsePass}
                    onChange={handleChange}
                    name="checkpointIssues.falsePass"
                  />} 
                  label="查核點判斷為達標" 
                />
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.checkpointIssues.falseFail}
                    onChange={handleChange}
                    name="checkpointIssues.falseFail"
                  />} 
                  label="查核點判斷未未達標" 
                />
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.checkpointIssues.unreasonableDelay}
                    onChange={handleChange}
                    name="checkpointIssues.unreasonableDelay"
                  />} 
                  label="查核點延遲不合理" 
                />
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.checkpointIssues.unclearDelay}
                    onChange={handleChange}
                    name="checkpointIssues.unclearDelay"
                  />} 
                  label="查核點延遲不明確" 
                />
                <FormControlLabel 
                  control={<Checkbox 
                    checked={formData.checkpointIssues.other}
                    onChange={handleChange}
                    name="checkpointIssues.other"
                  />} 
                  label="其他" 
                />
                {formData.checkpointIssues.other && (
                  <TextField
                    fullWidth
                    name="checkpointOtherDetails"
                    label="請說明其他問題"
                    multiline
                    rows={2}
                    value={otherIssueDetails.checkpointOtherDetails}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{ mt: 1, mb: 2 }}
                  />
                )}
              </FormGroup>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            {/* Suggestions */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                補充建議
              </Typography>
              <Typography variant="body2" paragraph>
                如有其他未在上方評估項目中涵蓋的技術功能改進建議，請在此補充
              </Typography>
              
              <TextField
                fullWidth
                name="technicalSuggestions"
                label="技術功能改進建議"
                multiline
                rows={4}
                value={formData.technicalSuggestions}
                onChange={handleChange}
                placeholder="請提供具體的技術功能改進建議..."
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                size="large"
                disabled={isLoading}
                sx={{ 
                  py: 1.5, 
                  px: 4, 
                  borderRadius: 2,
                  fontSize: '1rem'
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  '提交回饋'
                )}
              </Button>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                component={RouterLink}
                to="/poc-evaluation-results"
                startIcon={<AssessmentIcon />}
                color="secondary"
                sx={{ textDecoration: 'none', mt: 2 }}
              >
                查看已提交的評估結果
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default POCEvaluationForm; 